/**
 * Juurisyyt v2's facilitator panel (#1474, specs/workshop-modules/juurisyyt.md v2, "The facilitator's
 * panel"), copied from the v1 panel (screens/systemic-v2/JuurisyytAdmin.jsx) so the two change apart.
 * The root causes sit behind one fold, closed by default. Each row shows its title, the room's vote
 * (the share that said osuu, with the votes) and its Tietopohjaisuus, with the facilitator's own
 * Hyväksy / En osaa sanoa / Hylkää beside it; a tap on the title opens the rest: the problem and obstacle, the lens,
 * what the obstacle's writers and the other pairs said, earlier versions, and putting it on the
 * wall. The facilitator's decision works after "Lukitse arviot" too (the lock stops the room's
 * voting); a reject after the lock removes the root cause from the map at once. Hyväksy / Hylkää kaikki
 * arvioimattomat decide every root cause the AI added that has no decision yet, after asking; a
 * pair's own root cause is never touched.
 */
import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { ChevronDown, ChevronRight } from 'lucide-react';
import API_ENDPOINTS from '../../../config/api';
import { useTranslation } from '../../../hooks/useTranslation';
import useContentRefresh from '../../../hooks/useContentRefresh';
import { useConfirm } from '../../../components/DialogProvider';
import { v2Json } from '../v2Fetch';

const Wrap = styled.div`display: flex; flex-direction: column; gap: 10px; color: #e2e8f0;`;
const Bar = styled.div`display: flex; flex-wrap: wrap; align-items: center; gap: 8px 16px; font-size: 14px; color: #cbd5e1;`;
const Fold = styled.button`
  all: unset; cursor: pointer; display: flex; align-items: center; gap: 8px; min-height: 48px; padding: 0 14px;
  border-radius: 10px; border: 1px solid rgba(148,163,184,0.35); font-weight: 600; font-size: 15px;
`;
const Row = styled.div`
  display: grid; grid-template-columns: 1fr auto; gap: 8px 12px; align-items: center;
  padding: 8px 12px; border-radius: 10px; border: 1px solid rgba(148,163,184,0.25); background: rgba(15,23,42,0.6);
  opacity: ${(p) => (p.$faded ? 0.55 : 1)};
  @media (max-width: 720px) { grid-template-columns: 1fr; }
`;
const Head = styled.button`
  all: unset; cursor: pointer; display: flex; flex-direction: column; gap: 2px; min-height: 40px; justify-content: center;
  b { font-weight: 600; font-size: 15px; line-height: 1.35; text-decoration: ${(p) => (p.$struck ? 'line-through' : 'none')}; }
  span { font-size: 13px; color: #94a3b8; font-variant-numeric: tabular-nums; }
`;
const Badge = styled.span`
  display: inline-block; margin-left: 8px; padding: 1px 8px; border-radius: 999px; font-size: 12px; font-weight: 600; color: #e2e8f0;
  background: ${(p) => (p.$o === 'keep' ? 'rgba(16,185,129,0.25)' : p.$o === 'reject' ? 'rgba(248,113,113,0.25)' : p.$o === 'unsure' ? 'rgba(148,163,184,0.25)' : 'rgba(56,189,248,0.25)')};
`;
const Buttons = styled.div`display: flex; gap: 6px;`;
const Btn = styled.button`
  min-height: 44px; padding: 0 14px; border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 600; color: #e2e8f0;
  border: 1px solid ${(p) => (p.$danger ? 'rgba(248,113,113,0.5)' : 'rgba(148,163,184,0.4)')};
  background: ${(p) => (p.$on ? (p.$danger ? 'rgba(248,113,113,0.25)' : 'rgba(16,185,129,0.25)') : 'transparent')};
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;
const Detail = styled.div`grid-column: 1 / -1; display: flex; flex-direction: column; gap: 4px; font-size: 13px; color: #94a3b8;`;
const Area = styled.textarea`
  width: 100%; min-height: 70px; padding: 8px; border-radius: 8px; font-size: 14px; font-family: inherit;
  border: 1px solid rgba(148,163,184,0.4); background: rgba(0,0,0,0.3); color: #e2e8f0;
`;
const Note = styled.div`font-size: 13px; color: ${(p) => (p.$error ? '#fca5a5' : '#cbd5e1')};`;

const post = (url, body) => v2Json(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

function CauseRow({ card, focused, instanceId, onChanged, onError }) {
  const { t } = useTranslation();
  const k = (key, vars) => t(`juurisyytV2.admin.${key}`, vars);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const outcome = card.review?.outcome || null;
  const send = async (url, body) => {
    setBusy(true);
    try { await post(url, { instanceId, ...body }); await onChanged(); } catch (err) { onError(err.message); } finally { setBusy(false); }
  };
  const decide = (o) => send(API_ENDPOINTS.JUURISYYT_V2_DECISIONS(), { nodeId: card.id, outcome: outcome === o ? 'clear' : o });
  const vote = card.approval ? k('vote', { pct: card.approval.pct, votes: card.approval.votes }) : k('noVotes');
  const grounding = card.grounding == null ? k('noGrounding') : k('grounding', { score: card.grounding });
  const v = card.verdicts || {};
  return (
    <Row $faded={outcome === 'reject' || card.culled}>
      <Head type="button" $struck={outcome === 'reject' || card.culled} aria-expanded={open} onClick={() => setOpen((x) => !x)}>
        <b>
          {card.title}
          {card.culled ? <Badge $o="reject">{k('culled')}</Badge> : outcome ? <Badge $o={outcome}>{k(`outcomes.${outcome}`)}</Badge> : null}
        </b>
        <span>{vote} · {grounding}</span>
      </Head>
      <Buttons>
        <Btn type="button" disabled={busy} $on={outcome === 'keep'} aria-pressed={outcome === 'keep'} onClick={() => decide('keep')}>{k('accept')}</Btn>
        <Btn type="button" disabled={busy} $on={outcome === 'unsure'} aria-pressed={outcome === 'unsure'} onClick={() => decide('unsure')}>{k('unsure')}</Btn>
        <Btn type="button" disabled={busy} $danger $on={outcome === 'reject'} aria-pressed={outcome === 'reject'} onClick={() => decide('reject')}>{k('reject')}</Btn>
      </Buttons>
      {open ? (
        <Detail>
          <span>{[card.kipupiste, card.anchor].filter(Boolean).join(' → ')}{card.lensName ? ` · ${card.lensName}` : ''}</span>
          <span>{k('tally', { approve: v.approve || 0, reject: v.reject || 0 })}</span>
          {card.split ? <span>{k('split', { selfApprove: card.split.self.approve || 0, selfReject: card.split.self.reject || 0, outApprove: card.split.outside.approve || 0, outReject: card.split.outside.reject || 0 })}</span> : null}
          {(card.earlier || []).map((e) => <span key={e.version}>{k('earlier', { version: e.version, pct: e.approval?.pct ?? '—' })}</span>)}
          {card.review?.decidedBy === 'facilitator' ? <span>{k('decidedBy', { by: card.review.by || '—' })}</span> : null}
          {!card.culled ? (
            <Buttons>
              <Btn type="button" disabled={busy} onClick={() => send(API_ENDPOINTS.SYSTEMIC_V2_REVIEW_FOCUS(), { nodeId: focused ? null : card.id })}>
                {focused ? k('offWall') : k('onWall')}
              </Btn>
            </Buttons>
          ) : null}
        </Detail>
      ) : null}
    </Row>
  );
}

/** @param {{ instanceId: string }} props */
export default function JuurisyytV2Admin({ instanceId }) {
  const { t } = useTranslation();
  const k = (key, vars) => t(`juurisyytV2.admin.${key}`, vars);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [unlockReason, setUnlockReason] = useState(null);
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();

  const load = useCallback(async () => {
    if (!instanceId) return;
    try {
      setData(await v2Json(API_ENDPOINTS.JUURISYYT_V2_ADMIN(instanceId)));
      setError(null);
    } catch (err) { setError(err.message); }
  }, [instanceId]);
  useEffect(() => { load(); }, [load]);
  useContentRefresh(instanceId, ['systemic_v2'], load, { minIntervalMs: 3000 });

  const unlock = async () => {
    const reason = unlockReason?.trim();
    if (!reason) return;
    try {
      await post(API_ENDPOINTS.SYSTEMIC_V2_REVIEW_UNLOCK(), { instanceId, reason });
      setUnlockReason(null);
      await load();
    } catch (err) { setError(err.message); }
  };

  // Hyväksy / Hylkää / En osaa sanoa kaikki arvioimattomat: the AI's root causes with no decision yet; a pair's own never
  const undecided = (data?.cards || []).filter((c) => c.addedBy === 'ai' && !c.review && !c.culled).length;
  const decideAll = async (outcome) => {
    const [label, question] = { keep: ['acceptAll', 'acceptAllConfirm'], reject: ['rejectAll', 'rejectAllConfirm'], unsure: ['unsureAll', 'unsureAllConfirm'] }[outcome];
    const ok = await confirm({
      message: k(question, { count: undecided }),
      confirmLabel: k(label),
      destructive: outcome === 'reject',
    });
    if (!ok) return;
    setBusy(true);
    try { await post(API_ENDPOINTS.JUURISYYT_V2_DECISIONS_UNDECIDED(), { instanceId, outcome }); await load(); } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  if (!data) return error ? <Note $error>{error}</Note> : <Note>{t('juurisyyV2.loading')}</Note>;
  return (
    <Wrap>
      <Bar>
        <span>{k('summary', data.summary)}</span>
        {data.locked
          ? <span>{k('locked', { at: new Date(data.lockedAt).toLocaleString() })} {unlockReason === null ? <Btn type="button" onClick={() => setUnlockReason('')}>{k('unlock')}</Btn> : null}</span>
          : <span>{k('open')}</span>}
      </Bar>
      {data.locked && unlockReason !== null ? (
        <div>
          <Area autoFocus value={unlockReason} placeholder={k('unlockReason')} aria-label={k('unlockReason')} onChange={(e) => setUnlockReason(e.target.value)} />
          <Buttons>
            <Btn type="button" disabled={!unlockReason.trim()} onClick={unlock}>{k('unlock')}</Btn>
            <Btn type="button" onClick={() => setUnlockReason(null)}>{t('common.cancel')}</Btn>
          </Buttons>
        </div>
      ) : null}
      {error ? <Note $error role="alert">{error}</Note> : null}
      <Fold type="button" aria-expanded={open} onClick={() => setOpen((x) => !x)}>
        {open ? <ChevronDown size={18} aria-hidden /> : <ChevronRight size={18} aria-hidden />}
        {k('fold', { count: data.cards.length })}
      </Fold>
      {open ? (
        <Bar>
          <Btn type="button" disabled={busy || !undecided} onClick={() => decideAll('keep')}>{k('acceptAll')}</Btn>
          <Btn type="button" disabled={busy || !undecided} onClick={() => decideAll('unsure')}>{k('unsureAll')}</Btn>
          <Btn type="button" $danger disabled={busy || !undecided} onClick={() => decideAll('reject')}>{k('rejectAll')}</Btn>
          <span>{k('undecided', { count: undecided })}</span>
        </Bar>
      ) : null}
      {open ? data.cards.map((c) => (
        <CauseRow key={c.id} card={c} focused={data.focusNodeId === c.id} instanceId={instanceId} onChanged={load} onError={setError} />
      )) : null}
    </Wrap>
  );
}
